import { useEffect, useMemo } from 'react'
import { fetchExperienceById, fetchExperienceDates, fetchTransferOptions } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import { mapExperienceToBookingView, getExperienceDatesKey } from 'lib/experiences/experienceUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getLoyaltyTransferExperienceId } from 'loyalty/constants/loyaltyBenefitsConstants'

interface HomeToAirportTransferData {
  fetchingExperience: boolean
  fetchingDates: boolean
  fetchingTransferOptions: boolean
  redemptionLocations: Array<App.ExperienceRedemptionLocation>
  selectedRedemptionLocation?: App.ExperienceRedemptionLocation
  availableDates: Array<App.ExperienceAvailabilityDate>
  transferOptions: Array<App.ExperienceTransferOption>
  experienceBookingView?: App.ExperienceBookingView
}

/**
 * Hook to fetch transfer experience data for home-to-airport loyalty transfers.
 *
 * Fetches the loyalty magic experience and its dates. Transfer options use the same
 * fetch as the experience offer form (fetchTransferOptions by experienceId); options
 * are stable across location/date for this experience.
 *
 * @param selectedAirportId - The ID of the airport/redemption location selected by the customer
 * @param selectedDate - The date selected by the customer (YYYY-MM-DD format)
 */
export default function useHomeToAirportTransferData(
  selectedAirportId?: string,
  _selectedDate?: string,
): HomeToAirportTransferData {
  const dispatch = useAppDispatch()
  const experienceId = getLoyaltyTransferExperienceId()

  const {
    experience,
    fetchingExperience,
    experienceDates,
    transferOptions,
    fetchingTransferOptions,
  } = useAppSelector((state) => {
    const transferState = state.experience.experienceTransferOptions[experienceId]
    return {
      experience: state.experience.experiences[experienceId],
      fetchingExperience: state.experience.fetchingExperiences[experienceId] ?? false,
      experienceDates: state.experience.experienceDates[experienceId],
      transferOptions: transferState?.transfers ?? EmptyArray,
      fetchingTransferOptions: transferState?.fetching ?? false,
    }
  }, shallowEqual)

  useEffect(() => {
    dispatch(fetchExperienceById(experienceId))
  }, [dispatch, experienceId])

  useEffect(() => {
    dispatch(fetchTransferOptions(experienceId))
  }, [dispatch, experienceId])

  const redemptionLocations = experience?.redemptionLocations ?? EmptyArray

  const selectedRedemptionLocation = useMemo(() => {
    if (!selectedAirportId) return undefined
    return redemptionLocations.find(loc => loc.id === selectedAirportId)
  }, [redemptionLocations, selectedAirportId])

  const datesKey = useMemo(() => {
    if (!experience || !selectedRedemptionLocation) return undefined
    return getExperienceDatesKey(experienceId, {
      redemptionLocationId: selectedRedemptionLocation.id,
    })
  }, [experience, selectedRedemptionLocation, experienceId])

  useEffect(() => {
    if (experience && selectedRedemptionLocation && datesKey && !experienceDates?.[datesKey]) {
      dispatch(fetchExperienceDates(experienceId, {
        redemptionLocationId: selectedRedemptionLocation.id,
      }))
    }
  }, [dispatch, experience, selectedRedemptionLocation, datesKey, experienceDates, experienceId])

  const availableDates = datesKey ? (experienceDates?.[datesKey]?.dates ?? EmptyArray) : EmptyArray
  const fetchingDates = datesKey ? !!experienceDates?.[datesKey]?.fetching : false

  const experienceBookingView = useMemo((): App.ExperienceBookingView | undefined => {
    if (!experience) return undefined

    const bookingView = mapExperienceToBookingView(experience)

    // Override the airport with coordinates from the selected redemption location
    if (selectedRedemptionLocation && bookingView.originalExperience) {
      // Use city name as display, state as code (we don't have actual airport codes)
      const airport: App.Airport = {
        code: selectedRedemptionLocation.state ?? '',
        name: selectedRedemptionLocation.city ?? selectedRedemptionLocation.name,
        latitude: selectedRedemptionLocation.latitude,
        longitude: selectedRedemptionLocation.longitude,
      }
      return {
        ...bookingView,
        originalExperience: {
          ...bookingView.originalExperience,
          airport,
        },
      }
    }

    return bookingView
  }, [experience, selectedRedemptionLocation])

  return {
    fetchingExperience,
    fetchingDates,
    fetchingTransferOptions,
    redemptionLocations,
    selectedRedemptionLocation,
    availableDates,
    transferOptions,
    experienceBookingView,
  }
}
